import React, { useEffect, useState, useRef } from 'react';
import {
  Title,
  Text,
  Card,
  Group,
  Stack,
  Button,
  Badge,
  Loader,
  Alert,
  Paper,
  Table,
  Collapse,
  Box,
  NumberInput,
  SimpleGrid,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconSparkles, IconChevronDown, IconChevronUp, IconSettings } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { 
  getDesignedShifts, 
  selectShiftPattern, 
  generateShiftPatterns, 
  getDefaultDateRange,
  getOptimizerConfig,
  updateOptimizerConfig 
} from '../api/prediction-api';
import type { DesignedShift } from '../types/prediction-types';
import VerticalCalendar from '../components/VerticalCalendar';
import ShiftTimeline from '../components/ShiftTimeline';

export default function DesignedShiftsPage() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selecting, setSelecting] = useState<number | null>(null);
  const [shifts, setShifts] = useState<DesignedShift[]>([]);
  const [skipped, setSkipped] = useState<any[]>([]);
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const [datesLoaded, setDatesLoaded] = useState(false);
  
  // Optimizer config state - now holds all config values
  const [config, setConfig] = useState<Record<string, { value: number; description: string; updated_at: string }>>({});
  const [configOriginal, setConfigOriginal] = useState<Record<string, number>>({});
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState<string | null>(null);
  const [configExpanded, setConfigExpanded] = useState(false);
  
  // Track initial load to prevent double-fetching
  const initialLoadComplete = useRef(false);
  const prevStartDate = useRef<Date | null>(null);
  const prevEndDate = useRef<Date | null>(null);

  // Fetch default dates on mount
  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const defaults = await getDefaultDateRange();
        setStartDate(dayjs(defaults.start_date).toDate());
        setEndDate(dayjs(defaults.end_date).toDate());
        setDatesLoaded(true);
      } catch (error) {
        console.error('Failed to load default dates, using fallback:', error);
        // Fallback to current defaults
        setStartDate(new Date());
        setEndDate(dayjs().add(4, 'weeks').toDate());
        setDatesLoaded(true);
      }
    };
    loadDefaults();
  }, []);

  // Load optimizer config on mount
  useEffect(() => {
    const loadConfig = async () => {
      setConfigLoading(true);
      try {
        const configData = await getOptimizerConfig();
        setConfig(configData);
        
        // Store original values for comparison
        const originalValues: Record<string, number> = {};
        Object.keys(configData).forEach(key => {
          originalValues[key] = configData[key].value;
        });
        setConfigOriginal(originalValues);
      } catch (error) {
        console.error('Failed to load optimizer config:', error);
        notifications.show({
          title: 'Warning',
          message: 'Could not load optimizer configuration',
          color: 'yellow',
        });
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, []);

  // On page load, generate patterns keeping existing ones (only after dates are initially loaded)
  useEffect(() => {
    if (datesLoaded && startDate && endDate && !initialLoadComplete.current) {
      console.log('Initial load - generating patterns');
      generatePatternsKeepExisting();
      initialLoadComplete.current = true;
      prevStartDate.current = startDate;
      prevEndDate.current = endDate;
    }
  }, [datesLoaded, startDate, endDate]);

  // Fetch data when dates actually change (not on initial load)
  useEffect(() => {
    if (initialLoadComplete.current && datesLoaded && startDate && endDate) {
      const startDateChanged = prevStartDate.current?.getTime() !== startDate.getTime();
      const endDateChanged = prevEndDate.current?.getTime() !== endDate.getTime();
      
      if (startDateChanged || endDateChanged) {
        console.log('📅 Dates changed, fetching data...');
        fetchData();
        prevStartDate.current = startDate;
        prevEndDate.current = endDate;
      }
    }
  }, [startDate, endDate, datesLoaded]);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('📥 Fetching shifts...');
      console.log('Date range:', dayjs(startDate).format('YYYY-MM-DD'), 'to', dayjs(endDate).format('YYYY-MM-DD'));
      
      const response = await getDesignedShifts({
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
      });

      console.log('📊 Fetch response:', response);
      console.log('📊 Shifts count:', response.results?.length || 0);
      console.log('📊 Skipped count:', (response as any).skipped?.length || 0);
      
      setShifts(response.results);
      setSkipped((response as any).skipped || []);
    } catch (error: any) {
      console.error('❌ Fetch error:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to load designed shifts',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate patterns keeping existing ones (called on page load)
  const generatePatternsKeepExisting = async () => {
    setGenerating(true);
    try {
      console.log('🔧 Checking for missing shift patterns...');
      console.log('Date range:', dayjs(startDate).format('YYYY-MM-DD'), 'to', dayjs(endDate).format('YYYY-MM-DD'));
      
      const response = await generateShiftPatterns({
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
        keep_existing: true,  // ✅ Only generate for dates without existing shifts
      });
      
      console.log('✅ Generation response:', response);

      // Fetch updated data (no notification for page load)
      await fetchData();
    } catch (error: any) {
      console.error('❌ Generation error:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to generate shift patterns',
        color: 'red',
      });
    } finally {
      setGenerating(false);
    }
  };

  // Generate patterns (called when button clicked - regenerates ALL dates)
  const generatePatterns = async () => {
    setGenerating(true);
    try {
      console.log('🔧 Generating shift patterns...');
      console.log('Date range:', dayjs(startDate).format('YYYY-MM-DD'), 'to', dayjs(endDate).format('YYYY-MM-DD'));
      
      const response = await generateShiftPatterns({
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
        // ✅ No keep_existing parameter - regenerates ALL dates
      });
      
      console.log('✅ Generation response:', response);

      notifications.show({
        title: 'Success',
        message: 'Shift patterns generated successfully',
        color: 'green',
      });

      // Fetch updated data
      await fetchData();
    } catch (error: any) {
      console.error('❌ Generation error:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to generate shift patterns',
        color: 'red',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectPattern = async (patternId: number, date: string) => {
    setSelecting(patternId);
    try {
      await selectShiftPattern(patternId);
      
      notifications.show({
        title: 'Pattern Selected',
        message: `Pattern selected for ${dayjs(date).format('MMM D, YYYY')}`,
        color: 'green',
      });
      
      await fetchData();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to select pattern',
        color: 'red',
      });
    } finally {
      setSelecting(null);
    }
  };

  const handleSaveConfig = async (key: string, value: number) => {
    setConfigSaving(key);
    try {
      const result = await updateOptimizerConfig(key, value);
      
      // Update config with new value and timestamp
      setConfig(prev => ({
        ...prev,
        [key]: {
          ...prev[key],
          value: result.value,
          updated_at: result.updated_at
        }
      }));
      
      // Update original values
      setConfigOriginal(prev => ({ ...prev, [key]: result.value }));
      
      notifications.show({
        title: 'Configuration Saved',
        message: `${key.replace(/_/g, ' ')} updated to ${result.value}`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save configuration',
        color: 'red',
      });
      
      // Revert to original value on error
      setConfig(prev => ({
        ...prev,
        [key]: { ...prev[key], value: configOriginal[key] }
      }));
    } finally {
      setConfigSaving(null);
    }
  };

  const handleConfigChange = (key: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }));
  };

  const handleDateClick = (date: string) => {
    setExpandedDate(expandedDate === date ? null : date);
  };

  // Group shifts by date and pattern
  const shiftsByDate = shifts.reduce((acc, shift) => {
    const dateKey = shift.date;
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: dateKey,
        patterns: {},
      };
    }

    const patternKey = shift.pattern_id.toString(); // Use pattern_id as unique key
    if (!acc[dateKey].patterns[patternKey]) {
      acc[dateKey].patterns[patternKey] = {
        pattern_id: shift.pattern_id,
        pattern_name: shift.pattern_name,
        shift_type: shift.shift_type, // Add shift_type for display
        is_selected: shift.is_selected,
        shifts: [],
        total_revenue: shift.pattern_revenue,
        total_wages: shift.pattern_wages,
        total_profit: shift.pattern_profit,
        meets_2x: shift.meets_2x,
        fully_covered: shift.fully_covered,
        threshold_coefficient: (shift as any).threshold_coefficient,
        lost_revenue: (shift as any).lost_revenue,
        clusters_skipped: (shift as any).clusters_skipped,
      };
    }
    
    acc[dateKey].patterns[patternKey].shifts.push(shift);
    return acc;
  }, {} as Record<string, any>);

  const sortedDates = Object.keys(shiftsByDate).sort();

  // Create summary rows - ONE row per date (combining all patterns)
  const datesSummary: any[] = [];
  sortedDates.forEach(date => {
    const patterns = Object.values(shiftsByDate[date].patterns) as any[];
    const selectedPatterns = patterns.filter(p => p.is_selected === 1);
    
    if (selectedPatterns.length === 0) return; // Skip dates with no selected patterns
    
    // Combine all selected patterns for this date
    const totalShifts = selectedPatterns.reduce((sum, p) => sum + p.shifts.length, 0);
    const totalRevenue = selectedPatterns.reduce((sum, p) => sum + p.total_revenue, 0);
    const totalWages = selectedPatterns.reduce((sum, p) => sum + p.total_wages, 0);
    const totalProfit = selectedPatterns.reduce((sum, p) => sum + p.total_profit, 0);
    const totalLostRevenue = selectedPatterns.reduce((sum, p) => sum + (p.lost_revenue || 0), 0);
    const totalClustersSkipped = selectedPatterns.reduce((sum, p) => sum + (p.clusters_skipped || 0), 0);
    const thresholdCoefficient = selectedPatterns[0]?.threshold_coefficient || 1; // Same for all patterns on same date
    
    // Pattern name summary
    const patternNames = selectedPatterns.map(p => 
      selectedPatterns.length > 1 
        ? `${p.pattern_name} (${p.shift_type})`
        : p.pattern_name
    ).join(', ');
    
    datesSummary.push({
      date,
      selectedPattern: patternNames,
      shiftCount: totalShifts,
      revenue: totalRevenue,
      wages: totalWages,
      profit: totalProfit,
      lost_revenue: totalLostRevenue,
      clusters_skipped: totalClustersSkipped,
      threshold_coefficient: thresholdCoefficient,
      meets_2x: selectedPatterns.every(p => p.meets_2x === 1) ? 1 : 0,
      fully_covered: selectedPatterns.every(p => p.fully_covered === 1) ? 1 : 0,
      allPatterns: patterns, // All patterns for this date (for expansion)
    });
  });

  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Shifts Design</Title>
        <Text c="dimmed">Review and select shift patterns for upcoming dates</Text>
      </div>

      {/* Optimizer Configuration */}
      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group 
            gap="xs" 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setConfigExpanded(!configExpanded)}
          >
            <IconSettings size={20} />
            <Title order={4}>Optimizer Configuration</Title>
            {configExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
          </Group>
          
          <Collapse in={configExpanded}>
            {configLoading ? (
              <Loader size="sm" />
            ) : (
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                {Object.keys(config).length > 0 && Object.entries(config).map(([key, data]) => {
                  const currentValue = data.value;
                  const originalValue = configOriginal[key] || 0;
                  const hasChanged = currentValue !== originalValue;
                  const isSaving = configSaving === key;
                  
                  return (
                    <div key={key}>
                      <Group gap="sm" align="flex-end">
                        <div style={{ flex: 1 }}>
                          <NumberInput
                            label={key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                            description={data.description}
                            value={currentValue}
                            onChange={(val) => handleConfigChange(key, typeof val === 'number' ? val : 0)}
                            min={0}
                            max={key.includes('hours') ? 12 : key.includes('minutes') ? 60 : 10}
                            step={key.includes('coefficient') ? 0.1 : 1}
                            decimalScale={key.includes('coefficient') ? 1 : 0}
                            disabled={configSaving !== null}
                          />
                          {data.updated_at && (
                            <Text size="xs" c="dimmed" mt={4}>
                              Last updated: {dayjs(data.updated_at).format('MMM D, h:mm A')}
                            </Text>
                          )}
                        </div>
                        <Button
                          onClick={() => handleSaveConfig(key, currentValue)}
                          loading={isSaving}
                          disabled={!hasChanged || (configSaving !== null && configSaving !== key)}
                          leftSection={<IconCheck size={16} />}
                          size="sm"
                        >
                          Save
                        </Button>
                      </Group>
                    </div>
                  );
                })}
              </SimpleGrid>
            )}
          </Collapse>
        </Stack>
      </Paper>

      {/* Date Range and Generate */}
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap">
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              value={startDate}
              onChange={(date) => date && setStartDate(date)}
              maxDate={endDate || undefined}
            />
            <DatePickerInput
              label="End Date"
              value={endDate}
              onChange={(date) => date && setEndDate(date)}
              minDate={startDate || undefined}
            />
          </Group>
          <Button
            leftSection={<IconSparkles size={16} />}
            onClick={generatePatterns}
            loading={generating}
          >
            Generate
          </Button>
        </Group>
      </Card>

      {loading || generating ? (
        <Paper shadow="sm" p="xl" withBorder>
          <Group justify="center">
            <Loader />
            <Text>{generating ? 'Generating shift patterns...' : 'Loading shift patterns...'}</Text>
          </Group>
        </Paper>
      ) : shifts.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} title="No Shifts" color="blue">
          <Stack gap="sm">
            <Text>No designed shifts found for the selected date range.</Text>
            {startDate && endDate && (
              <Text size="sm" c="dimmed">
                Date range: {dayjs(startDate).format('MMM D, YYYY')} - {dayjs(endDate).format('MMM D, YYYY')}
              </Text>
            )}
            <Button
              leftSection={<IconSparkles size={16} />}
              onClick={generatePatterns}
              loading={generating}
              mt="sm"
            >
              Generate Shift Patterns
            </Button>
          </Stack>
        </Alert>
      ) : (
        <>
          {/* Vertical Calendar View */}
          {startDate && endDate && (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <VerticalCalendar 
                shifts={shifts} 
                startDate={startDate}
                endDate={endDate}
                skipped={skipped}
                onDateClick={handleDateClick} 
              />
            </Card>
          )}

          {/* Details Table */}
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={4} mb="md">Shift Details</Title>
            
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Selected Pattern</Table.Th>
                  <Table.Th>Shifts</Table.Th>
                  <Table.Th>Revenue</Table.Th>
                  <Table.Th>Wages</Table.Th>
                  <Table.Th>Profit</Table.Th>
                  <Table.Th>Threshold</Table.Th>
                  <Table.Th>Lost Revenue</Table.Th>
                  <Table.Th>Skipped</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datesSummary.map((day) => (
                  <React.Fragment key={day.date}>
                    <Table.Tr
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleDateClick(day.date)}
                    >
                      <Table.Td>
                        <Text fw={600}>{dayjs(day.date).format('ddd, MMM D')}</Text>
                      </Table.Td>
                      <Table.Td>{day.selectedPattern}</Table.Td>
                      <Table.Td>{day.shiftCount}</Table.Td>
                      <Table.Td>${day.revenue.toFixed(2)}</Table.Td>
                      <Table.Td>${day.wages.toFixed(2)}</Table.Td>
                      <Table.Td>
                        <Text c={day.profit > 0 ? 'green' : 'red'} fw={600}>
                          ${day.profit.toFixed(2)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light">
                          {day.threshold_coefficient}x
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {day.lost_revenue > 0 ? (
                          <Text size="sm" c="orange">
                            ${day.lost_revenue.toFixed(2)}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {day.clusters_skipped > 0 ? (
                          <Badge size="sm" color="orange" variant="light">
                            {day.clusters_skipped}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">—</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Badge size="xs" color={day.meets_2x ? 'green' : 'red'}>
                            {day.meets_2x ? '✅' : '❌'} 2x
                          </Badge>
                          <Badge size="xs" color={day.fully_covered ? 'green' : 'yellow'}>
                            {day.fully_covered ? '✅' : '⚠️'} Coverage
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {expandedDate === day.date ? (
                          <IconChevronUp size={16} />
                        ) : (
                          <IconChevronDown size={16} />
                        )}
                      </Table.Td>
                    </Table.Tr>
                    
                    {/* Expanded pattern comparison */}
                    <Table.Tr>
                      <Table.Td colSpan={8} p={0}>
                        <Collapse in={expandedDate === day.date}>
                          <Box p="md" bg="gray.0">
                            {/* Combined card for all selected patterns */}
                            {(() => {
                              const selectedPatterns = day.allPatterns.filter((p: any) => p.is_selected === 1);
                              
                              if (selectedPatterns.length === 0) return null;
                              
                              // Combine all shifts from selected patterns
                              const allSelectedShifts = selectedPatterns.flatMap((p: any) => p.shifts);
                              
                              // Calculate combined totals
                              const totalRevenue = selectedPatterns.reduce((sum: number, p: any) => sum + p.total_revenue, 0);
                              const totalWages = selectedPatterns.reduce((sum: number, p: any) => sum + p.total_wages, 0);
                              const totalProfit = selectedPatterns.reduce((sum: number, p: any) => sum + p.total_profit, 0);
                              const meets2x = selectedPatterns.every((p: any) => p.meets_2x === 1);
                              const fullyCovered = selectedPatterns.every((p: any) => p.fully_covered === 1);
                              
                              return (
                                <Box mb="md">
                                  <Card
                                    shadow="sm"
                                    padding="lg"
                                    radius="md"
                                    withBorder
                                    style={{
                                      borderColor: '#40c057',
                                      borderWidth: 2,
                                    }}
                                  >
                                    <Stack gap="md">
                                      <Group justify="space-between">
                                        <div>
                                          <Text size="lg" fw={600}>
                                            {selectedPatterns.length > 1 
                                              ? `Combined: ${selectedPatterns.map((p: any) => `${p.pattern_name} (${p.shift_type})`).join(', ')}`
                                              : selectedPatterns[0].pattern_name
                                            }
                                          </Text>
                                          {selectedPatterns.length === 1 && (
                                            <Text size="sm" c="dimmed">
                                              {selectedPatterns[0].shift_type.charAt(0).toUpperCase() + selectedPatterns[0].shift_type.slice(1)} Shift
                                            </Text>
                                          )}
                                          <Badge color="green" leftSection={<IconCheck size={12} />}>
                                            SELECTED
                                          </Badge>
                                        </div>
                                      </Group>

                                      {/* Combined shift timeline */}
                                      <ShiftTimeline 
                                        shifts={allSelectedShifts}
                                        totalRevenue={totalRevenue}
                                      />

                                      {/* Individual shift details */}
                                      <Stack gap="xs">
                                        {allSelectedShifts.map((shift: any, idx: number) => (
                                          <Paper key={idx} p="sm" withBorder>
                                            <Text size="sm" fw={600}>
                                              Shift {idx + 1}: {shift.start_time} - {shift.end_time}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                              {shift.employees} employee • Revenue: ${shift.revenue?.toFixed(2) || '0.00'} • Wages: ${shift.wages?.toFixed(2) || '0.00'} • Profit: ${shift.profit?.toFixed(2) || '0.00'}
                                            </Text>
                                          </Paper>
                                        ))}
                                      </Stack>

                                      {/* Combined totals */}
                                      <Box>
                                        <Text size="sm" fw={700} mb="xs">TOTALS</Text>
                                        <Group gap="md">
                                          <Text size="sm" c="blue" fw={600}>
                                            REVENUE: ${totalRevenue.toFixed(2)}
                                          </Text>
                                          <Text size="sm" c="orange" fw={600}>
                                            WAGES: ${totalWages.toFixed(2)}
                                          </Text>
                                          <Text size="sm" c={totalProfit >= 0 ? 'green' : 'red'} fw={600}>
                                            PROFIT: ${totalProfit.toFixed(2)}
                                          </Text>
                                        </Group>
                                        <Group gap="xs" mt="xs">
                                          <Badge color={meets2x ? 'green' : 'red'}>
                                            {meets2x ? '✅' : '❌'} 2x Rule
                                          </Badge>
                                          <Badge color={fullyCovered ? 'green' : 'yellow'}>
                                            {fullyCovered ? '✅' : '⚠️'} Coverage
                                          </Badge>
                                        </Group>
                                      </Box>
                                    </Stack>
                                  </Card>
                                </Box>
                              );
                            })()}
                            
                            {/* Alternative patterns */}
                            {(() => {
                              const unselectedPatterns = day.allPatterns.filter((p: any) => p.is_selected === 0);
                              
                              if (unselectedPatterns.length === 0) return null;
                              
                              return (
                                <>
                                  <Text size="sm" fw={600} mb="xs">Alternative Patterns:</Text>
                                  <Stack gap="md">
                                    {unselectedPatterns.map((pattern: any) => (
                                      <Card
                                        key={`${day.date}-${pattern.pattern_id}`}
                                        shadow="sm"
                                        padding="lg"
                                        radius="md"
                                        withBorder
                                      >
                                        <Stack gap="md">
                                          <Group justify="space-between">
                                            <div>
                                              <Text size="lg" fw={600}>
                                                {pattern.pattern_name}
                                              </Text>
                                              <Text size="sm" c="dimmed">
                                                {pattern.shift_type.charAt(0).toUpperCase() + pattern.shift_type.slice(1)} Shift
                                              </Text>
                                            </div>
                                            <Button
                                              size="xs"
                                              variant="light"
                                              onClick={() => handleSelectPattern(pattern.pattern_id, day.date)}
                                              loading={selecting === pattern.pattern_id}
                                            >
                                              Select
                                            </Button>
                                          </Group>

                                          {/* Timeline Visualization */}
                                          <ShiftTimeline
                                            shifts={pattern.shifts}
                                            totalRevenue={pattern.total_revenue}
                                          />

                                          {/* Individual shift details */}
                                          <Stack gap="xs">
                                            {pattern.shifts
                                              .sort((a: DesignedShift, b: DesignedShift) => a.shift_number - b.shift_number)
                                              .map((shift: DesignedShift, idx: number) => (
                                                <Paper key={`${day.date}-${pattern.pattern_id}-shift-${shift.shift_number}-${idx}`} p="sm" withBorder>
                                                  <Text size="sm" fw={600}>
                                                    Shift {shift.shift_number}: {shift.start_time} - {shift.end_time}
                                                  </Text>
                                                  <Text size="xs" c="dimmed">
                                                    {shift.employees} employee • Revenue: ${shift.revenue.toFixed(2)} • Wages: ${shift.wages.toFixed(2)} • Profit: ${shift.profit.toFixed(2)}
                                                  </Text>
                                                </Paper>
                                              ))}
                                          </Stack>

                                          {/* Totals */}
                                          <Box>
                                            <Text size="sm" fw={700} mb="xs">TOTALS</Text>
                                            <Group gap="md">
                                              <Text size="sm" c="blue" fw={600}>
                                                REVENUE: ${pattern.total_revenue.toFixed(2)}
                                              </Text>
                                              <Text size="sm" c="orange" fw={600}>
                                                WAGES: ${pattern.total_wages.toFixed(2)}
                                              </Text>
                                              <Text size="sm" c={pattern.total_profit >= 0 ? 'green' : 'red'} fw={600}>
                                                PROFIT: ${pattern.total_profit.toFixed(2)}
                                              </Text>
                                            </Group>
                                            <Group gap="xs" mt="xs">
                                              <Badge color={pattern.meets_2x ? 'green' : 'red'}>
                                                {pattern.meets_2x ? '✅' : '❌'} 2x Rule
                                              </Badge>
                                              <Badge color={pattern.fully_covered ? 'green' : 'yellow'}>
                                                {pattern.fully_covered ? '✅' : '⚠️'} Coverage
                                              </Badge>
                                            </Group>
                                          </Box>
                                        </Stack>
                                      </Card>
                                    ))}
                                  </Stack>
                                </>
                              );
                            })()}
                          </Box>
                        </Collapse>
                      </Table.Td>
                    </Table.Tr>
                  </React.Fragment>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </>
      )}
    </Stack>
  );
}
